import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Plus, Search, Edit3, Trash2, Eye, EyeOff, FileText,
  Calendar, User, Folder, Tag, ChevronLeft, ChevronRight,
  Sparkles, ExternalLink, Filter, AlertCircle
} from 'lucide-react';
import { adminApi } from '../../lib/adminApi';
import { getBlogImageUrl } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import BlogNavTabs from './BlogNavTabs';
import './AdminBlog.css';

export default function AdminBlogs() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [categories, setCategories] = useState([]);
  const [authors, setAuthors] = useState([]);

  // Search & Filters
  const search = searchParams.get('search') || '';
  const statusFilter = searchParams.get('status') || 'all';
  const categoryFilter = searchParams.get('category') || '';
  const authorFilter = searchParams.get('author') || '';
  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);
  const [refresh, setRefresh] = useState(0);
  const limit = 10; // Exactly 10 blogs per page as requested

  const [busyActionId, setBusyActionId] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // Fetch categories and authors for filters
  useEffect(() => {
    adminApi.get('/admin/blog-categories').then(r => setCategories(r.data || [])).catch(() => {});
    adminApi.get('/admin/blog-authors').then(r => setAuthors(r.data || [])).catch(() => {});
  }, []);

  // Fetch blog posts
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');

    const timer = setTimeout(() => {
      adminApi.get('/admin/blogs', {
        params: {
          page,
          limit,
          search: search.trim(),
          status: statusFilter,
          category_id: categoryFilter || undefined,
          author_id: authorFilter || undefined
        }
      })
      .then(res => {
        if (!active) return;
        setPosts(res.data.items || []);
        setTotal(res.data.total || 0);
        setTotalPages(res.data.total_pages || 1);
      })
      .catch(err => {
        if (!active) return;
        setError(err.response?.data?.detail || "Failed to load blog posts.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    }, 200);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [page, search, statusFilter, categoryFilter, authorFilter, refresh]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value && value !== 'all') {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    next.set('page', '1');
    setSearchParams(next);
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    const next = new URLSearchParams(searchParams);
    next.set('page', String(newPage));
    setSearchParams(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleVisibility = async (post) => {
    setBusyActionId(post.id);
    const nextStatus = post.visibility === 'visible' ? 'hidden' : 'visible';
    try {
      await adminApi.patch(`/admin/blogs/${post.id}/visibility`, { visibility: nextStatus });
      setRefresh(value => value + 1);
    } catch (err) {
      alert("Could not update visibility. Please try again.");
    } finally {
      setBusyActionId(null);
    }
  };

  const handleDelete = async (postId) => {
    setBusyActionId(postId);
    try {
      await adminApi.delete(`/admin/blogs/${postId}`);
      if (posts.length === 1 && page > 1) handlePageChange(page - 1);
      else setRefresh(value => value + 1);
      setDeleteConfirmId(null);
    } catch (err) {
      alert("Failed to delete blog post.");
    } finally {
      setBusyActionId(null);
    }
  };

  const hasFiltersApplied = search || statusFilter !== 'all' || categoryFilter || authorFilter;

  return (
    <main className="space-y-6">
      <BlogNavTabs
        title="Blog posts"
        description="Write, organize, and publish SEO-optimized articles for your clients and brand."
        showCreateButton={true}
      />

      {/* Filter and Search Bar */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex flex-col md:flex-row gap-3 items-stretch md:items-center justify-between">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            aria-label="Search blog posts"
            type="search"
            value={search}
            onChange={(e) => {
              updateParam('search', e.target.value);
            }}
            placeholder="Filter blog posts by title or tag..."
            className="w-full pl-9 pr-4 py-2 text-sm border border-slate-300 rounded-lg focus:border-amber-500 focus:outline-none"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Status Filter */}
          <select
            aria-label="Filter by status"
            value={statusFilter}
            onChange={(e) => {
              updateParam('status', e.target.value);
            }}
            className="text-xs border border-slate-300 rounded-lg px-3 py-2 bg-white text-slate-700 font-medium focus:border-amber-500"
          >
            <option value="all">All Status</option>
            <option value="visible">Visible</option>
            <option value="hidden">Hidden (Draft)</option>
          </select>

          {/* Category Filter */}
          <select
            aria-label="Filter by category"
            value={categoryFilter}
            onChange={(e) => {
              updateParam('category', e.target.value);
            }}
            className="text-xs border border-slate-300 rounded-lg px-3 py-2 bg-white text-slate-700 font-medium focus:border-amber-500"
          >
            <option value="">All Categories</option>
            {categories.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>

          {/* Author Filter */}
          <select
            aria-label="Filter by author"
            value={authorFilter}
            onChange={(e) => {
              updateParam('author', e.target.value);
            }}
            className="text-xs border border-slate-300 rounded-lg px-3 py-2 bg-white text-slate-700 font-medium focus:border-amber-500"
          >
            <option value="">All Authors</option>
            {authors.map(a => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>

          {hasFiltersApplied && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSearchParams({});
              }}
              className="text-xs text-slate-500 hover:text-slate-800"
            >
              Reset filters
            </Button>
          )}
        </div>
      </div>

      {/* Error Notice */}
      {error && (
        <div role="alert" className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center gap-2 text-sm">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {/* Loading Skeleton */}
      {loading && !posts.length && (
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-slate-500">
          <div className="admin-spinner mb-3" />
          <p className="text-sm font-medium">Loading blog posts...</p>
        </div>
      )}

      {/* Shopify Empty State (Image 1) */}
      {!loading && !error && posts.length === 0 && !hasFiltersApplied && (
        <div className="blog-empty-card">
          <div className="blog-empty-illustration">
            <div className="blog-empty-icon-wrap flex flex-col items-center justify-center relative p-3">
              <div className="blog-empty-badge">B I U</div>
              <div className="w-12 h-8 bg-sky-200 rounded mb-2 flex items-center justify-center text-sky-600">
                <FileText size={20} />
              </div>
              <div className="w-14 h-2 bg-slate-300 rounded-full mb-1" />
              <div className="w-10 h-2 bg-slate-200 rounded-full" />
            </div>
          </div>

          <h3 className="text-xl font-bold text-slate-900 mb-2">Write a blog post</h3>
          <p className="text-sm text-slate-500 max-w-md mx-auto mb-6">
            Blog posts are a great way to build a community around your products, boost organic search traffic, and establish authority.
          </p>

          <div className="flex items-center justify-center gap-3">
            <Button
              variant="outline"
              onClick={() => navigate('/admin/blog-categories')}
              className="text-xs font-semibold px-4 py-2"
            >
              Manage categories
            </Button>
            <Button
              onClick={() => navigate('/admin/blogs/new')}
              className="admin-primary text-xs font-semibold px-5 py-2 shadow"
            >
              <Plus size={15} className="mr-1" />
              Create blog post
            </Button>
          </div>
        </div>
      )}

      {/* No matching results */}
      {!loading && !error && posts.length === 0 && hasFiltersApplied && (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center text-slate-500 space-y-3">
          <FileText size={36} className="mx-auto text-slate-300" />
          <h4 className="text-base font-semibold text-slate-800">No blog posts found</h4>
          <p className="text-xs text-slate-500">Try adjusting your search keywords or filter settings.</p>
        </div>
      )}

      {/* Blog Posts Table */}
      {posts.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm border-collapse">
              <thead>
                <tr className="bg-slate-50/80 border-b border-slate-200 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  <th className="py-3.5 px-4 w-12">Thumbnail</th>
                  <th className="py-3.5 px-4">Title & Details</th>
                  <th className="py-3.5 px-4">Category</th>
                  <th className="py-3.5 px-4">Author</th>
                  <th className="py-3.5 px-4">Status</th>
                  <th className="py-3.5 px-4">Date</th>
                  <th className="py-3.5 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {posts.map((post) => (
                  <tr key={post.id} className="hover:bg-slate-50/60 transition-colors">
                    {/* Thumbnail */}
                    <td className="py-3.5 px-4 align-top">
                      {post.featured_image ? (
                        <img
                          src={getBlogImageUrl(post.featured_image)}
                          alt={post.title}
                          className="w-12 h-12 object-cover rounded-lg border border-slate-200"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-slate-100 rounded-lg border border-slate-200 flex items-center justify-center text-slate-400">
                          <FileText size={20} />
                        </div>
                      )}
                    </td>

                    {/* Title & Slug */}
                    <td className="py-3.5 px-4 align-top max-w-md">
                      <button
                        type="button"
                        onClick={() => navigate(`/admin/blogs/edit/${post.id}`)}
                        className="text-left font-semibold text-slate-900 hover:text-amber-600 transition-colors block text-sm"
                      >
                        {post.title}
                      </button>
                      <span className="text-[11px] text-slate-400 font-mono block mt-0.5">
                        /blogs/{post.slug}
                      </span>
                      {post.excerpt && (
                        <p className="text-xs text-slate-500 line-clamp-1 mt-1">
                          {post.excerpt}
                        </p>
                      )}
                      {post.tags && post.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {post.tags.slice(0, 3).map((t, idx) => (
                            <span key={idx} className="inline-block text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full border border-slate-200">
                              #{t}
                            </span>
                          ))}
                          {post.tags.length > 3 && (
                            <span className="text-[10px] text-slate-400 self-center">
                              +{post.tags.length - 3} more
                            </span>
                          )}
                        </div>
                      )}
                    </td>

                    {/* Category */}
                    <td className="py-3.5 px-4 align-top whitespace-nowrap text-xs">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-50 text-amber-900 font-medium border border-amber-200/60">
                        <Folder size={12} className="text-amber-600" />
                        {post.category_name || "Uncategorized"}
                      </span>
                    </td>

                    {/* Author */}
                    <td className="py-3.5 px-4 align-top whitespace-nowrap text-xs text-slate-700">
                      <div className="flex items-center gap-1.5 font-medium">
                        <div className="w-6 h-6 rounded-full bg-slate-200 text-slate-700 flex items-center justify-center text-[10px] font-bold">
                          {(post.author_name || "A")[0].toUpperCase()}
                        </div>
                        <span>{post.author_name || "Admin"}</span>
                      </div>
                    </td>

                    {/* Status */}
                    <td className="py-3.5 px-4 align-top whitespace-nowrap">
                      <button
                        type="button"
                        disabled={busyActionId === post.id}
                        onClick={() => toggleVisibility(post)}
                        title="Click to toggle visibility"
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold cursor-pointer transition-colors ${
                          post.visibility === 'visible'
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100'
                            : 'bg-slate-100 text-slate-600 border border-slate-300 hover:bg-slate-200'
                        }`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${post.visibility === 'visible' ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                        <span>{post.visibility === 'visible' ? 'Visible' : 'Hidden'}</span>
                      </button>
                    </td>

                    {/* Date */}
                    <td className="py-3.5 px-4 align-top whitespace-nowrap text-xs text-slate-500">
                      {post.published_at ? (
                        <span>{new Date(post.published_at).toLocaleDateString()}</span>
                      ) : post.created_at ? (
                        <span>{new Date(post.created_at).toLocaleDateString()}</span>
                      ) : '—'}
                    </td>

                    {/* Actions */}
                    <td className="py-3.5 px-4 align-top text-right whitespace-nowrap">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/admin/blogs/edit/${post.id}`)}
                          className="h-8 w-8 p-0 text-slate-600 hover:text-amber-600 hover:bg-amber-50"
                          title="Edit post"
                        >
                          <Edit3 size={15} />
                        </Button>

                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={busyActionId === post.id}
                          onClick={() => setDeleteConfirmId(post.id)}
                          className="h-8 w-8 p-0 text-slate-400 hover:text-red-600 hover:bg-red-50"
                          title="Delete post"
                        >
                          <Trash2 size={15} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination: Exactly 10 blogs per page with total count */}
          <div className="border-t border-slate-200 px-4 py-3.5 flex flex-col sm:flex-row items-center justify-between gap-3 bg-slate-50/50">
            <p className="text-xs text-slate-600">
              Showing <span className="font-semibold">{Math.min(1 + (page - 1) * limit, total)}</span> to{' '}
              <span className="font-semibold">{Math.min(page * limit, total)}</span> of{' '}
              <span className="font-semibold">{total}</span> blog posts (10 per page)
            </p>

            <div className="flex items-center gap-1.5">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => handlePageChange(page - 1)}
                className="h-8 px-2 text-xs flex items-center gap-1"
              >
                <ChevronLeft size={14} />
                <span>Previous</span>
              </Button>

              <div className="flex items-center gap-1 mx-1">
                {Array.from({ length: totalPages }, (_, idx) => idx + 1)
                  .filter(pNum => pNum === 1 || pNum === totalPages || Math.abs(pNum - page) <= 1)
                  .map((pNum, index, arr) => (
                    <React.Fragment key={pNum}>
                      {index > 0 && arr[index - 1] !== pNum - 1 && (
                        <span className="text-xs text-slate-400 px-1">…</span>
                      )}
                      <button
                        type="button"
                        onClick={() => handlePageChange(pNum)}
                        className={`w-8 h-8 rounded-lg text-xs font-semibold transition-colors ${
                          pNum === page
                            ? 'bg-amber-500 text-slate-900 shadow-sm'
                            : 'text-slate-600 hover:bg-slate-200 bg-white border border-slate-200'
                        }`}
                      >
                        {pNum}
                      </button>
                    </React.Fragment>
                  ))}
              </div>

              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => handlePageChange(page + 1)}
                className="h-8 px-2 text-xs flex items-center gap-1"
              >
                <span>Next</span>
                <ChevronRight size={14} />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6 space-y-4">
            <h4 className="font-bold text-slate-900 text-base">Delete blog post?</h4>
            <p className="text-xs text-slate-600 leading-relaxed">
              Are you sure you want to delete this blog post? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDeleteConfirmId(null)}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={busyActionId === deleteConfirmId}
                onClick={() => handleDelete(deleteConfirmId)}
                className="admin-danger"
              >
                {busyActionId === deleteConfirmId ? 'Deleting...' : 'Delete Post'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
